import { CloseOutlined, DeleteOutlineOutlined } from "@mui/icons-material";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from "@mui/material";
import { ReactNode, useEffect, useRef } from "react";

import { LoadingButton } from "@mui/lab";

interface DeleteDialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  content: ReactNode;
  onDelete: () => void;
  isLoading?: boolean;
  isSuccess?: boolean;
  errorMessage?: string | null;
  confirmButtonText?: string;
  /** Test-id prefix. When set, the dialog and its controls get
   *  `{tid}-dialog` / `{tid}-confirm-btn` / `{tid}-cancel-btn` /
   *  `{tid}-close-btn` so a caller can address its own confirmation without
   *  matching on button copy. Naming follows docs/TESTID_INJECTION_SPEC.md. */
  tid?: string;
}

export default function DeleteDialog({
  open,
  onClose,
  title,
  content,
  onDelete,
  isLoading = false,
  isSuccess = false,
  errorMessage = null,
  confirmButtonText = "I want to delete this item",
  tid,
}: DeleteDialogProps) {
  // Close only on the false->true edge of isSuccess, not whenever it is true.
  // React Query mutation state stays `isSuccess` after a successful delete; if
  // a caller keeps this dialog mounted and reopens it (e.g. to delete a second
  // item) without resetting the mutation, a level-triggered close would slam
  // the dialog shut the instant it reopens. Edge-triggering closes on the
  // success that the user just confirmed and ignores stale success.
  const prevSuccess = useRef(isSuccess);
  useEffect(() => {
    if (isSuccess && !prevSuccess.current) {
      onClose();
    }
    prevSuccess.current = isSuccess;
  }, [isSuccess, onClose]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        data-testid={tid ? `${tid}-dialog` : undefined}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%", px: 2 }}
        >
          <DialogTitle
            sx={{
              flex: 1,
              whiteSpace: "normal",
              wordWrap: "break-word",
              overflow: "visible",
            }}
          >
            {title}
          </DialogTitle>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            data-attr="shared:btn:close-delete-dialog"
            data-testid={tid ? `${tid}-close-btn` : undefined}
          >
            <CloseOutlined />
          </IconButton>
        </Stack>

        <DialogContent sx={{ pt: 1 }} dividers>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          {content}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {/* Cancel is focused on open (P0: delete dialogs focus the safe
              action, not the destructive confirm). */}
          <Button
            autoFocus
            color="inherit"
            onClick={onClose}
            disabled={isLoading}
            data-attr="shared:btn:cancel-delete"
            data-testid={tid ? `${tid}-cancel-btn` : undefined}
          >
            Cancel
          </Button>
          <LoadingButton
            color="error"
            variant="contained"
            disableElevation
            startIcon={<DeleteOutlineOutlined />}
            loading={isLoading}
            onClick={onDelete}
            data-attr="shared:btn:confirm-delete"
            data-testid={tid ? `${tid}-confirm-btn` : undefined}
          >
            {confirmButtonText}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
